import { useNavigate } from "react-router-dom";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../common/infrastructure/components/RenderWhen";
import { routePaths } from "../../../common/infrastructure/routes/routePaths";
import { Icon, IconType } from "../../../common/icons/Icon";
import { ReviewForm } from "../components/ReviewForm/ReviewForm";
import { StarRating } from "../components/StarRating/StarRating";
import { getThankYouMessage } from "../utils/reviewUtility";
import { useReviewInvite } from "./hooks/useReviewInvite";
import styles from "./ReviewInvitePage.module.scss";

/** The page the "Как беше?" e-mail opens (/review?token=…&rating=N). */
export const ReviewInvitePage = () => {
  const {
    isLoading,
    isInvalid,
    isSubmitted,
    submittedRating,
    submittedComment,
    isSaving,
    isEdit,
    studioName,
    googleReviewUrl,
    initialValues,
    submit,
  } = useReviewInvite();
  const navigate = useNavigate();

  // With a Google link that is the main ask, so going home steps back to an outline button.
  const homeButton = (design: "default" | "outline", width = "auto") => (
    <DefaultButton
      label="Към началото"
      design={design}
      width={width}
      onClick={() => navigate(routePaths.home)}
    />
  );

  const renderContent = () => {
    if (isLoading) {
      return <p className={styles.root_text}>Зареждане…</p>;
    }

    if (isInvalid) {
      return (
        <>
          <p className={styles.root_text}>
            Линкът е изтекъл или невалиден. Можете да оставите ревю от „Моите
            резервации“.
          </p>
          {homeButton("default")}
        </>
      );
    }

    if (isSubmitted) {
      return (
        <div className={styles.thanks}>
          {/* A gold tick that draws itself once; no pulsing. */}
          <div className={styles.thanks_badge} aria-hidden="true">
            <svg viewBox="0 0 24 24" className={styles.thanks_badge_tick}>
              <path d="M6 12.5l4 4 8-9" />
            </svg>
          </div>

          <h2 className={styles.root_title}>Благодарим за ревюто!</h2>
          <StarRating
            value={submittedRating}
            size="medium"
            className={styles.thanks_stars}
          />
          <p className={styles.root_text}>
            {getThankYouMessage(submittedRating, studioName)}
          </p>

          <RenderWhen condition={!!submittedComment}>
            <blockquote className={styles.thanks_comment}>
              {submittedComment}
            </blockquote>
          </RenderWhen>

          <RenderWhen condition={!!googleReviewUrl}>
            <div className={styles.thanks_google}>
              <div className={styles.thanks_google_header}>
                <Icon type={IconType.Google} width={22} height={22} />
                <span className={styles.thanks_google_title}>
                  Ще ни помогнете ли и в Google?
                </span>
              </div>
              <p className={styles.thanks_google_text}>
                {`Ревю в Google помага на повече хора да открият ${studioName}. Отнема по-малко от минута.`}
              </p>
            </div>
          </RenderWhen>

          {/* One column, so both buttons are the same width. */}
          <div className={styles.thanks_actions}>
            <RenderWhen condition={!!googleReviewUrl}>
              <DefaultButton
                label="Оставете ревю и в Google"
                width="100%"
                onClick={() =>
                  window.open(googleReviewUrl!, "_blank", "noopener,noreferrer")
                }
              />
            </RenderWhen>
            {homeButton(googleReviewUrl ? "outline" : "default", "100%")}
          </div>
        </div>
      );
    }

    return (
      <>
        <h2 className={styles.root_title}>{`Как беше в ${studioName}?`}</h2>
        <ReviewForm
          initialValues={initialValues}
          isEdit={isEdit}
          isSaving={isSaving}
          onSubmit={submit}
        />
      </>
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.root_card}>{renderContent()}</div>
    </div>
  );
};
